import { getAuthHeaders } from './auth';
import type { FitReplayResponse, FitReplayMode, Segment } from './api';

const API_BASE = import.meta.env.VITE_API_URL;

// ============ Types ============

export type TrainingDataSource = 'manual' | 'strava' | 'fit' | 'garmin';

export interface TrainingSettings {
  ftp: number | null;
  ftpSource: TrainingDataSource | null;
  ftpUpdatedAt: string | null;
  maxHeartRate: number | null;
  maxHeartRateSource: TrainingDataSource | null;
  maxHeartRateUpdatedAt: string | null;
  stravaConnected: boolean;
  stravaAthleteName: string | null;
  stravaFtpNotificationEnabled: boolean;
  garminConnected: boolean;
  garminUserId: string | null;
  autoCreateReplays: boolean;
}

export interface UpdateTrainingSettings {
  ftp?: number | null;
  maxHeartRate?: number | null;
  stravaFtpNotificationEnabled?: boolean;
  autoCreateReplays?: boolean;
}

export interface StravaActivity {
  id: string;
  name: string;
  type: string;
  sport_type: string;
  start_date: string;
  start_date_local: string;
  timezone: string;
  distance: number; // meters
  moving_time: number; // seconds
  elapsed_time: number; // seconds
  total_elevation_gain: number; // meters
  average_speed: number; // m/s
  max_speed: number; // m/s
  average_heartrate?: number;
  max_heartrate?: number;
  average_watts?: number;
  max_watts?: number;
  weighted_average_watts?: number;
  kilojoules?: number;
  average_cadence?: number;
  has_heartrate: boolean;
  device_watts: boolean;
  location_name?: string; // Reverse geocoded location name
  map?: {
    id: string;
    summary_polyline?: string;
    polyline?: string;
  };
}

export interface StravaActivitiesResponse {
  activities: StravaActivity[];
  page: number;
  perPage: number;
  hasMore: boolean;
}

export interface StravaStatus {
  connected: boolean;
  athleteId?: number;
  athleteName?: string;
  expiresAt?: string;
}

// ============ Training Settings API ============

/**
 * Get current user's training settings (FTP, Max HR, Strava connection status)
 */
export async function getTrainingSettings(): Promise<TrainingSettings> {
  const response = await fetch(`${API_BASE}/users/me/training-settings`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get training settings' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Update training settings (FTP, Max HR)
 */
export async function updateTrainingSettings(data: UpdateTrainingSettings): Promise<TrainingSettings> {
  const response = await fetch(`${API_BASE}/users/me/training-settings`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to update training settings' }));
    throw new Error(error.message);
  }
  return response.json();
}

// ============ Strava OAuth API ============

/**
 * Get URL to start Strava OAuth flow
 * User will be redirected to Strava for authorization
 * Includes JWT token as query param since browser redirect doesn't send headers
 */
export function getStravaConnectUrl(): string {
  const token = localStorage.getItem('pelo1-access-token');
  if (!token) {
    return `${API_BASE}/auth/strava`;
  }
  return `${API_BASE}/auth/strava?token=${encodeURIComponent(token)}`;
}

/**
 * Disconnect Strava account
 */
export async function disconnectStrava(): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE}/auth/strava`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to disconnect Strava' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Get Strava connection status
 */
export async function getStravaStatus(): Promise<StravaStatus> {
  const response = await fetch(`${API_BASE}/strava/status`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get Strava status' }));
    throw new Error(error.message);
  }
  return response.json();
}

// ============ Strava Activities API ============

/**
 * Get Strava activities list
 * @param page - Page number (default: 1)
 * @param perPage - Items per page (default: 20)
 * @param lang - Language for location names (default: 'en')
 */
export async function getStravaActivities(
  page = 1,
  perPage = 20,
  lang = 'en',
): Promise<StravaActivitiesResponse> {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
    lang,
  });
  const response = await fetch(`${API_BASE}/strava/activities?${params}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch Strava activities' }));
    throw new Error(error.message);
  }
  return response.json();
}

/**
 * Get single Strava activity details
 */
export async function getStravaActivity(activityId: string): Promise<StravaActivity> {
  const response = await fetch(`${API_BASE}/strava/activities/${activityId}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch Strava activity' }));
    throw new Error(error.message);
  }
  return response.json();
}

// ============ Strava Replay API ============

/**
 * Create a replay from Strava activities
 */
export async function createStravaReplay(
  name: string,
  activityIds: string[],
  mode: FitReplayMode = 'time_trial',
  segment?: Segment,
  gpxFile?: File,
): Promise<FitReplayResponse> {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('activityIds', JSON.stringify(activityIds));
  formData.append('mode', mode);

  if (segment) {
    formData.append('segment', JSON.stringify(segment));
  }

  if (gpxFile) {
    formData.append('gpx', gpxFile);
  }

  const response = await fetch(`${API_BASE}/fit-replay/create-from-strava`, {
    method: 'POST',
    headers: getAuthHeaders(), // No Content-Type for FormData
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to create Strava replay' }));
    throw new Error(error.message);
  }

  return response.json();
}

// ============ Utilities ============

/**
 * Format distance from meters to km string
 */
export function formatDistance(meters: number): string {
  const km = meters / 1000;
  return `${km.toFixed(1)} km`;
}

/**
 * Format duration from seconds to HH:MM:SS or MM:SS string
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

/**
 * Format speed from m/s to km/h string
 */
export function formatSpeed(mps: number): string {
  const kmh = mps * 3.6;
  return `${kmh.toFixed(1)} km/h`;
}

/**
 * Format date to local date string
 */
export function formatActivityDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get activity type display name
 */
export function getActivityTypeName(type: string): string {
  const types: Record<string, string> = {
    Ride: '라이딩',
    VirtualRide: '버추얼 라이딩',
    EBikeRide: 'E-바이크',
    MountainBikeRide: 'MTB',
    GravelRide: '그래블',
    Run: '러닝',
    VirtualRun: '버추얼 러닝',
    Walk: '걷기',
    Hike: '하이킹',
    Swim: '수영',
  };
  return types[type] || type;
}

/**
 * Check if an activity is a cycling activity
 */
export function isCyclingActivity(type: string): boolean {
  return ['Ride', 'VirtualRide', 'EBikeRide', 'MountainBikeRide', 'GravelRide'].includes(type);
}
